import React from "react";
import { useMutation } from "@apollo/client";
import { usePostHog } from "posthog-js/react";
import { useSnackbar } from "components/haven-design/snackbar";
import { usePostgresViewer } from "components/viewer/context";
import {
  openEmbeddedPropertyContractSign,
  usePropertyContractHelloSign,
} from "utils/property-contract-embedded-sign";
import { getBrowserIanaTimeZone } from "utils/date";
import {
  MarkPropertyContractPendingShared,
  MarkPropertyContractPendingSharedVariables,
  PropertyContractSignUrlShared,
  PropertyContractSignUrlSharedVariables,
  ResendPropertyContractShared,
  ResendPropertyContractSharedVariables,
} from "types/gql-types";
import {
  ContractRow,
  MARK_PENDING_MUTATION,
  PROPERTY_CONTRACTS_QUERY,
  reminderResendUserMessage,
  REMINDER_ALREADY_SIGNED_MESSAGE,
  RESEND_MUTATION,
  SIGN_URL_MUTATION,
} from "./contracts-shared";

type RefetchContracts = () => Promise<unknown>;

/** Sign-now and send-reminder handlers for sent property contracts. */
export const useContractDetailsActions = (
  selectedContract: ContractRow | null,
  refetch: RefetchContracts,
) => {
  const viewer = usePostgresViewer();
  const posthog = usePostHog();
  const { openSnackbar } = useSnackbar();
  const [openHelloSign] = usePropertyContractHelloSign();

  const trackContractAction = React.useCallback(
    (
      action: "send_reminder" | "sign_now",
      outcome: "started" | "success" | "error",
      contract: ContractRow,
    ) => {
      posthog?.capture("property_contract_action", {
        action,
        outcome,
        contractId: contract.id,
        status: contract.status,
        propertyName: contract.propertyName,
      });
    },
    [posthog],
  );
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [signedContractId, setSignedContractId] = React.useState<string | null>(null);
  const [reminderSentForContractId, setReminderSentForContractId] =
    React.useState<string | null>(null);
  const [reminderFailure, setReminderFailure] = React.useState<{
    contractId: string;
    message: string;
  } | null>(null);

  const [resendMutation] =
    useMutation<
    ResendPropertyContractShared,
    ResendPropertyContractSharedVariables
    >(RESEND_MUTATION, {
      refetchQueries: [{ query: PROPERTY_CONTRACTS_QUERY }],
      awaitRefetchQueries: true,
    });
  const [signUrlMutation] =
    useMutation<
    PropertyContractSignUrlShared,
    PropertyContractSignUrlSharedVariables
    >(SIGN_URL_MUTATION);
  const [markPendingMutation] =
    useMutation<
    MarkPropertyContractPendingShared,
    MarkPropertyContractPendingSharedVariables
    >(MARK_PENDING_MUTATION);

  React.useEffect(() => {
    setReminderSentForContractId(null);
    setReminderFailure(null);
    setSignedContractId(null);
  }, [selectedContract?.id]);

  const signerEmail = selectedContract?.signerEmail?.trim().toLowerCase() || "";
  const viewerEmail = viewer?.email?.trim().toLowerCase() || "";
  const signerLoggedIn = Boolean(
    selectedContract && signerEmail && viewerEmail && signerEmail === viewerEmail,
  );
  const showSignNow = Boolean(
    selectedContract &&
      signerLoggedIn &&
      selectedContract.status === "sent",
  );

  const handleResendConfirm = async (c: ContractRow) => {
    const resendId = c.id;
    if (!resendId) return;
    setReminderSentForContractId(null);
    setReminderFailure(null);
    setBusyId(resendId);
    trackContractAction("send_reminder", "started", c);
    try {
      await resendMutation({
        variables: {
          id: resendId,
          signerName: c.signerName ?? "",
          signerEmail: c.signerEmail ?? "",
        },
      });
      setReminderSentForContractId(resendId);
      trackContractAction("send_reminder", "success", c);
      openSnackbar({ message: "Reminder sent.", status: "success" });
    } catch (err: unknown) {
      const message = reminderResendUserMessage(err);
      setReminderFailure({ contractId: resendId, message });
      trackContractAction("send_reminder", "error", c);
      openSnackbar({
        message,
        status: message === REMINDER_ALREADY_SIGNED_MESSAGE ? "warning" : "error",
        duration: 4000,
      });
    } finally {
      setBusyId(null);
      refetch();
    }
  };

  const handleSignNow = async (c: ContractRow) => {
    const resendId = c.id;
    if (!resendId) return;
    setReminderSentForContractId(null);
    setReminderFailure(null);
    setBusyId(resendId);
    trackContractAction("sign_now", "started", c);
    try {
      await openEmbeddedPropertyContractSign({
        open: openHelloSign,
        prepareAndGetSignUrl: async () => {
          // Capture the signer's browser zone so the subscription start date is their local date.
          const result = await signUrlMutation({
            variables: { id: resendId, timeZone: getBrowserIanaTimeZone() },
          });
          const signUrl = result.data?.propertyContractSignUrl ?? "";
          return { signUrl };
        },
      });
      // The signer completed the embedded flow: move the contract from "sent" to
      // "pending" on the server (no-op if the HelloSign webhook already marked it
      // "signed"). This broadcasts so the sidebar reflects the pending label.
      try {
        await markPendingMutation({ variables: { id: resendId } });
      } catch {
        // Best-effort: the webhook remains the source of truth for the final status.
      }
      // Lock the UI on the modal's success callback so Sign Now / Delete Draft are
      // disabled immediately, without waiting for the server status refetch.
      setSignedContractId(resendId);
      trackContractAction("sign_now", "success", c);
      openSnackbar({ message: "Contract signed.", status: "success" });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to open signing flow";
      trackContractAction("sign_now", "error", c);
      openSnackbar({ message, status: "error", duration: 4000 });
    } finally {
      await refetch();
      setBusyId(null);
    }
  };

  return {
    busyId,
    signedContractId,
    reminderSentForContractId,
    reminderFailure,
    showSignNow,
    handleResendConfirm,
    handleSignNow,
  };
};
